import { QueryClientProvider } from "@tanstack/react-query";
import { httpBatchStreamLink, loggerLink } from "@trpc/client";
import { createTRPCQueryUtils, createTRPCReact } from "@trpc/react-query";
import SuperJSON from "superjson";

import type { AppRouter } from "@degaus/trpc";

import type { QueryClient } from "@tanstack/react-query";
import { getApiBaseUrl } from "@/common/api";
import { getSessionToken } from "@/common/auth";
import { checkIsWondatwinHost } from "@/common/wondatwin";
import { createQueryClient } from "./query-client";

let clientQueryClientSingleton: QueryClient | undefined = undefined;

export function getQueryClient(): QueryClient {
  if (typeof window === "undefined") {
    return createQueryClient();
  }
  return (clientQueryClientSingleton ??= createQueryClient());
}

export const api = createTRPCReact<AppRouter>({
  overrides: {
    useMutation: {
      async onSuccess(options) {
        await options.originalFn();
        // Invalidate all queries after a mutation, except:
        //  - lastStepOutput: refreshing it re-signs URLs / causes churn.
        //  - the gateway-session identity + presence queries (useSession.ts):
        //    a data mutation must NEVER refetch the session. They have their own
        //    5-min staleTime + an explicit refresh path (useSessionRefresh), and
        //    refetching them here churns RouterContextSyncInner mid-load (the
        //    onboarding remount loop). Keys mirror GATEWAY_*_QUERY_KEY.
        await options.queryClient.invalidateQueries({
          predicate: (query) =>
            !query.queryKey.includes("lastStepOutput") &&
            !query.queryKey.includes("gateway-session-identity") &&
            !query.queryKey.includes("gateway-session-present"),
        });
      },
    },
  },
});

async function getHeaders(): Promise<Headers> {
  const headers = new Headers();
  headers.set("x-trpc-source", "web");
  // Wonda Twin only calls public procedures. Its auth CTAs leave for wonda.sh,
  // so avoid probing a product-host session endpoint or attaching credentials.
  if (
    typeof window !== "undefined" &&
    checkIsWondatwinHost(window.location.hostname)
  ) {
    return headers;
  }
  const bearerToken = await getSessionToken();
  headers.set("authorization", `Bearer ${bearerToken}`);
  return headers;
}

// The API serves tRPC under /api/trpc (api-service index.ts:
// app.route("/api", apiApp) + apiApp.route("/trpc", trpc)), and its permissive
// origin:"*" CORS is scoped to /api/trpc/*. So we must hit that exact path or
// the cross-origin preflight has no Access-Control-Allow-Origin and fails.
// VITE_API_URL points at the API in one of two shapes: already suffixed with
// /api (local dev + worktrees, see scripts/worktree-setup.md: https://<host>/api)
// or the bare origin (Cloud Run: https://staging.api.wondercat.ai). Normalize by
// stripping a trailing /api, then always append /api/trpc so both resolve to the
// same mount. When VITE_API_URL is empty (the .env.example default / Amplify
// same-origin serving) keep the relative /trpc path unchanged.
const apiBase = getApiBaseUrl();
const trpcUrl = apiBase
  ? `${apiBase.replace(/\/api\/?$/, "")}/api/trpc`
  : "/trpc";

const trpcClient = api.createClient({
  links: [
    loggerLink({
      enabled: (op) =>
        import.meta.env.MODE === "development" ||
        (op.direction === "down" && op.result instanceof Error),
    }),
    httpBatchStreamLink({
      transformer: SuperJSON,
      url: trpcUrl,
      headers: getHeaders,
    }),
  ],
});
export const trpcQueryUtils = createTRPCQueryUtils<AppRouter>({
  queryClient: getQueryClient(),
  client: trpcClient,
});

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <api.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </api.Provider>
    </QueryClientProvider>
  );
}
